import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const { language, t, switchTo } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine if we're on a dark page (home hero)
  const isDarkPage = location.pathname === '/';

  const navLinks = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/team', label: t.nav.team },
    { path: '/gallery', label: t.nav.gallery },
    { path: '/cercle', label: t.nav.clientele },
    { path: '/contact', label: t.nav.contact },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      data-testid="main-navigation"
    >
      {/* Floating glass pill on ALL screen sizes */}
      <div
        className={`mx-3 mt-3 lg:mx-6 lg:mt-4 rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.35)] border ${
          isDarkPage
            ? 'border-white/15'
            : 'border-[#8A8780]/25'
        }`}
        style={{
          background: isDarkPage
            ? 'rgba(26, 26, 24, 0.78)'
            : 'rgba(245, 240, 232, 0.88)',
          backdropFilter: 'blur(22px) saturate(1.15)',
          WebkitBackdropFilter: 'blur(22px) saturate(1.15)',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="flex items-center justify-between h-16 lg:h-[4.5rem]">
          {/* Logo - Playfair Display per brand book */}
          <Link to="/" className="flex-shrink-0 group" data-testid="nav-logo">
            <div className="text-left">
              <span className={`font-display text-xl tracking-[0.1em] transition-colors ${
                isDarkPage ? 'text-[#F5F0E8]' : 'text-[#1A1A18]'
              }`}>
                CASANOVA
              </span>
              <div className={`font-body font-extralight text-[9px] tracking-[0.22em] uppercase transition-colors ${
                isDarkPage ? 'text-[#8A8780]' : 'text-[#8A8780]'
              }`}>
                SALON · BARBIER
              </div>
            </div>
          </Link>

          {/* Desktop Nav Links - Center - Barlow Medium */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`link-underline font-body font-medium text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                  isActive(link.path)
                    ? (isDarkPage ? 'text-[#F5F0E8]' : 'text-[#1A1A18]')
                    : (isDarkPage ? 'text-[#8A8780] hover:text-[#F5F0E8]' : 'text-[#8A8780] hover:text-[#1A1A18]')
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side - Language Toggle + CTA */}
          <div className="flex items-center gap-6">
            {/* Language Toggle */}
            <div className={`lang-toggle hidden sm:flex ${isDarkPage ? 'lang-toggle-dark' : ''}`} data-testid="lang-toggle">
              <button
                onClick={() => switchTo('fr')}
                className={language === 'fr' ? 'active' : ''}
                data-testid="lang-fr"
              >
                FR
              </button>
              <span className="text-[#8A8780]">/</span>
              <button
                onClick={() => switchTo('en')}
                className={language === 'en' ? 'active' : ''}
                data-testid="lang-en"
              >
                EN
              </button>
            </div>

            {/* Desktop Book Now Button - Brick Orange */}
            <Link
              to="/booking"
              className="hidden md:inline-flex btn-cta items-center gap-2"
              data-testid="nav-book-button"
            >
              {t.nav.bookNow}
            </Link>

            {/* Mobile Menu Button — larger tap target, visible ring */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-11 h-11 rounded-full flex items-center justify-center border transition-all duration-300 active:scale-95 ${
                isDarkPage
                  ? 'text-[#F5F0E8] border-white/20 bg-white/5 hover:bg-white/10'
                  : 'text-[#1A1A18] border-[#1A1A18]/20 bg-[#1A1A18]/5 hover:bg-[#1A1A18]/10'
              }`}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Floating Parchment Window */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Dark backdrop - clickable to close */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden fixed inset-0 bg-[#1A1A18]/60 z-40 cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-menu-backdrop"
              />

              {/* Floating parchment menu */}
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden fixed top-[5.5rem] left-3 right-3 bg-[#F5F0E8] z-50 shadow-2xl rounded-2xl"
                data-testid="mobile-menu"
              >
                <div className="p-6">
                  {/* Logo in menu */}
                  <div className="text-center mb-6 pb-6 border-b border-[#8A8780]/20">
                    <span className="font-display text-2xl tracking-[0.1em] text-[#1A1A18]">CASANOVA</span>
                    <div className="font-body font-extralight text-[9px] tracking-[0.22em] text-[#8A8780] mt-1 uppercase">SALON · BARBIER</div>
                  </div>

                  <div className="space-y-1">
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={link.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`block py-3 font-display text-2xl tracking-tight border-b border-[#8A8780]/10 transition-colors ${
                            isActive(link.path) ? 'text-[#A14223]' : 'text-[#1A1A18] hover:text-[#A14223]'
                          }`}
                        >
                          {link.label}
                        </Link>
                      </motion.div>
                    ))}
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="mt-6 pt-6 border-t border-[#8A8780]/20"
                  >
                    <Link
                      to="/booking"
                      onClick={() => setMobileMenuOpen(false)}
                      className="btn-cta w-full flex items-center justify-center gap-3"
                    >
                      {t.nav.bookNow}
                      <ArrowRight size={14} />
                    </Link>

                    {/* Mobile Language Toggle */}
                    <div className="flex justify-center gap-4 mt-4">
                      <button
                        onClick={() => switchTo('fr')}
                        className={`font-body font-medium text-sm tracking-wider transition-colors ${language === 'fr' ? 'text-[#1A1A18]' : 'text-[#8A8780]'}`}
                      >
                        Français
                      </button>
                      <span className="text-[#8A8780]">|</span>
                      <button
                        onClick={() => switchTo('en')}
                        className={`font-body font-medium text-sm tracking-wider transition-colors ${language === 'en' ? 'text-[#1A1A18]' : 'text-[#8A8780]'}`}
                      >
                        English
                      </button>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
